import { useMemo, useContext } from 'react';
import styled from '@emotion/styled';
import type { Chat, ChatCitationType } from '@trinity/chat';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis';
import { KPIWidget } from '@trinity/chat-components/components/QuantAnalysis/KPIWidget';
import { NivoBarChart } from '@trinity/chat-components/components/Widgets/Charts/Bar';
import { NivoMultipleBarChart } from '@trinity/chat-components/components/Widgets/Charts/MultipleBar';
import { NivoPieChart } from '@trinity/chat-components/components/Widgets/Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Widgets/Charts/StackedBar';
import { ProgressBarWidget } from '@trinity/chat-components/components/Widgets/ProgressBarWidget.js';
import { SemiCircleProgressWidget } from '@trinity/chat-components/components/Widgets/SemiCircleProgressWidget.js';
import { useOptionsColors } from '@trinity/chat-components/containers/AppTheming/useOptionsColors';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';

type Props = {
  snippet: Chat.CitationSnippet<ChatCitationType.Dashboard>;
  link?: string | null;
};

/**
 * Renders a dashboard citation (widget or rich text)
 * - For widgets: Renders actual chart/KPI components
 * - For rich text: Renders markdown content
 */
export const DashboardCitation = ({ snippet, link }: Props) => {
  const metadata = snippet.metadata;

  // Resolve chart colors using the same logic as the segment dashboard:
  // honour the widget's saved brand theme and fall back to the app default palette.
  const { data: brandThemingData } = useContext(BrandThemingContext);
  const defaultColors = useOptionsColors();
  const chartColors = useMemo(() => {
    const { selectedThemeId } = metadata;
    if (!selectedThemeId || !brandThemingData?.categories) return defaultColors;
    for (const group of brandThemingData.categories) {
      const theme = group.items.find((item) => item.id === selectedThemeId);
      if (theme) return theme.colors;
    }
    return defaultColors;
  }, [metadata, brandThemingData, defaultColors]);

  const renderContent = useMemo(() => {
    // Rich text: render as markdown
    if (metadata.itemType === 'rich-text') {
      return <ContentWrapper>{snippet.text && <BasicMarkdown>{snippet.text}</BasicMarkdown>}</ContentWrapper>;
    }

    // Widget: render the actual chart/KPI component
    if (metadata.itemType === 'widget' && metadata.widgetData) {
      const chart = metadata.widgetData;

      switch (chart.type) {
        case 'bar': {
          const barChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoChartContainer><NivoBarChart chart={chart.chart} colors={barChartColors} /></NivoChartContainer>
            </ChartWrapper>
          );
        }

        case 'stacked-bar': {
          const barChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoChartContainer><NivoStackedBarChart chart={chart.chart} colors={barChartColors} /></NivoChartContainer>
            </ChartWrapper>
          );
        }

        case 'pie': {
          const pieChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoChartContainer><NivoPieChart chart={chart.chart} colors={pieChartColors} /></NivoChartContainer>
            </ChartWrapper>
          );
        }

        case 'multiple-clustered-bar':
        case 'multiple-stacked-bar': {
          const allKeys = new Set<string>();
          chart.chart.data.forEach((series) => {
            series.data.forEach((d) => allKeys.add(d.x));
          });
          const keys = Array.from(allKeys);
          const multipleBarChartColors = keys.map((_, i) => chartColors[i % chartColors.length]);

          return (
            <ChartWrapper>
              <ChartTitle>{chart.chart.title}</ChartTitle>
              <NivoChartContainer>
                <NivoMultipleBarChart
                  chart={chart.chart}
                  colors={multipleBarChartColors}
                  isStacked={chart.type === 'multiple-stacked-bar'}
                />
              </NivoChartContainer>
            </ChartWrapper>
          );
        }

        case 'kpi-widget': {
          return (
            <KPIWrapper>
              <KPIWidget
                title={chart.title}
                backgroundColor={chart.backgroundColor}
                values={chart.values}
                labels={chart.labels}
                insights={chart.insights}
              />
            </KPIWrapper>
          );
        }

        case 'data-table': {
          return (
            <TableWrapper>
              <AnalysisTable
                data={chart.data}
                headerBackgroundColor={chart.headerColor}
                initialSortConfig={chart.sortConfig}
                disableSorting={false}
                disableSelection={true}
              />
            </TableWrapper>
          );
        }

        case 'progress-bar-widget': {
          return (
            <ChartWrapper>
              <ChartTitle>{chart.title}</ChartTitle>
              <ProgressBarWidget
                colors={chart.colors}
                minValues={chart.minValues ?? []}
                maxValues={chart.maxValues ?? []}
                title={chart.title}
                backgroundColor={chart.backgroundColor}
                values={chart.values}
                labels={chart.labels}
                insights={chart.insights}
              />
            </ChartWrapper>
          );
        }

        case 'semi-circle-progress-widget': {
          return (
            <ChartWrapper>
              <ChartTitle>{chart.title}</ChartTitle>
              <SemiCircleProgressWidget
                colors={chart.colors}
                values={chart.values}
                minValues={chart.minValues ?? []}
                maxValues={chart.maxValues ?? []}
                title={chart.title}
                backgroundColor={chart.backgroundColor}
                labels={chart.labels}
                insights={chart.insights}
              />
            </ChartWrapper>
          );
        }

        default:
          return (
            <ContentWrapper>
              <NoContentMessage>Widget type not supported for preview</NoContentMessage>
            </ContentWrapper>
          );
      }
    }

    return (
      <ContentWrapper>
        <NoContentMessage>No content available</NoContentMessage>
      </ContentWrapper>
    );
  }, [metadata, snippet.text, chartColors]);

  // Append the specific widget ID as a query param so the dashboard page can
  // scroll directly to the cited widget rather than just the segment overview.
  const viewLink = useMemo(() => {
    if (!link) return null;
    if (!metadata.id) return link;
    const separator = link.includes('?') ? '&' : '?';
    return `${link}${separator}widgetId=${encodeURIComponent(metadata.id)}`;
  }, [link, metadata.id]);

  return (
    <StyledRoot>
      {renderContent}
      {viewLink && (
        <ViewButtonWrapper>
          <a href={viewLink} target="_blank" rel="noreferrer">
            <View>{`View >`}</View>
          </a>
        </ViewButtonWrapper>
      )}
    </StyledRoot>
  );
};

const StyledRoot = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContentWrapper = styled.div`
  min-height: 100px;
`;

const ChartWrapper = styled.div`
  min-height: 240px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--gray-xl, #e0e0e0);
  border-radius: 8px;
  background-color: #fff;
`;

const NivoChartContainer = styled.div`
  height: 320px;
  width: 100%;
`;

const ChartTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.palette.gray.dark1};
  margin: 0;
  padding: 0;
`;

const KPIWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const NoContentMessage = styled.div`
  color: ${(props) => props.theme.palette.gray.dark1};
  font-style: italic;
  text-align: center;
  padding: 16px;
`;

const ViewButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 8px;
`;

const View = styled.div`
  font-size: 13px;
  font-family: ${(props) => props.theme.fonts.semiBold};
`;
